import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  NIVEIS,
  RECURSOS,
  ROTULO_RECURSO,
  ganho,
  nivelOuNegado,
  type Recurso,
} from '@arena/shared/permissoes';
import type { MembroDaEquipe, PerfilNaMatriz } from '@arena/shared/schemas';
import { auditoriaApi, baixarArquivo, perfisApi } from '../api/acessos.js';
import { mensagemDoErro } from '../api/cliente.js';
import { useSessao } from '../auth/SessaoContext.jsx';
import { Erro, EsqueletoDeCard } from '../ui/estados.jsx';
import { Icone } from '../ui/Icone.js';
import {
  Avatar,
  Botao,
  BotaoDeIcone,
  Card,
  Feed,
  ItemDeFeed,
  Nota,
  Quem,
  Status,
  Tabela,
  Vazio,
} from '../ui/primitivos.jsx';
import { useToast } from '../ui/Toast.jsx';
import { classeDaCelula, quandoLocal, tituloDoEvento } from './formatos.js';
import { ModalDeCodigoDeSenha } from './ModalDeCodigoDeSenha.jsx';
import { ModalDeNovoPerfil } from './ModalDeNovoPerfil.jsx';
import { ModalDeSegundoFator } from './ModalDeSegundoFator.jsx';

/**
 * `#v-acessos` — a matriz editável, o card Segurança e o resumo da
 * trilha. O console de auditoria completo (com filtros) é uma tela à
 * parte: `PaginaDoConsole`, em `/auditoria` — o protótipo não a desenha,
 * só o feed resumido que está aqui.
 *
 * A regra de elevação é conferida duas vezes: aqui, de leve, para nem
 * deixar a pessoa TENTAR mudar a própria célula para algo maior (é
 * cosmético — usa o MESMO `ganho()` de `@arena/shared/permissoes` que o
 * servidor usa para decidir de verdade); e no servidor, que é quem
 * recusa de fato se este aviso for contornado.
 *
 * Ver: Arena_backend/Docs/plano/19-permissoes-auditoria.md
 */
export function PaginaDeAcessos() {
  const { eu, pode } = useSessao();
  const navegar = useNavigate();
  const avisar = useToast();
  const cliente = useQueryClient();

  const [erro, setErro] = useState<string | null>(null);
  const [novoPerfilAberto, setNovoPerfilAberto] = useState(false);
  const [segundoFatorAberto, setSegundoFatorAberto] = useState(false);
  const [renomeando, setRenomeando] = useState<{ id: string; nome: string } | null>(null);

  const podeCriar = pode('perfis', 'criar');
  const podeEditar = pode('perfis', 'editar');
  const podeExcluir = pode('perfis', 'excluir');

  const matrizQuery = useQuery({ queryKey: ['acessos', 'matriz'], queryFn: perfisApi.matriz });
  const equipeQuery = useQuery({ queryKey: ['acessos', 'equipe'], queryFn: perfisApi.equipe });
  // "Esqueci a senha": o Dono gera o código e entrega à pessoa. Não há
  // envio automático no produto — ver `ModalDeCodigoDeSenha`.
  const [codigoPara, setCodigoPara] = useState<MembroDaEquipe | null>(null);
  const segurancaQuery = useQuery({
    queryKey: ['acessos', 'seguranca'],
    queryFn: perfisApi.seguranca,
  });
  const auditoriaQuery = useQuery({
    queryKey: ['auditoria', 'resumo'],
    queryFn: () => auditoriaApi.pagina({}, undefined, 5),
  });

  function invalidarMatriz() {
    void cliente.invalidateQueries({ queryKey: ['acessos'] });
  }

  const alterarCelula = useMutation({
    mutationFn: ({ id, recurso, nivel }: { id: string; recurso: Recurso; nivel: string }) =>
      perfisApi.alterarCelula(id, recurso, nivel),
    onSuccess: invalidarMatriz,
    onError: (e) => setErro(mensagemDoErro(e)),
  });

  const renomear = useMutation({
    mutationFn: ({ id, nome }: { id: string; nome: string }) => perfisApi.renomear(id, nome),
    onSuccess: () => {
      invalidarMatriz();
      setRenomeando(null);
      avisar('Perfil renomeado.');
    },
    onError: (e) => setErro(mensagemDoErro(e)),
  });

  const desativar = useMutation({
    mutationFn: (id: string) => perfisApi.desativar(id),
    onSuccess: (r) => {
      invalidarMatriz();
      avisar(
        r.membrosAfetados > 0
          ? `"${r.perfil.nome}" desativado — ${r.membrosAfetados} ${r.membrosAfetados === 1 ? 'pessoa perdeu' : 'pessoas perderam'} o acesso.`
          : `"${r.perfil.nome}" desativado.`,
        'aviso',
      );
    },
    onError: (e) => setErro(mensagemDoErro(e)),
  });

  const reativar = useMutation({
    mutationFn: (id: string) => perfisApi.reativar(id),
    onSuccess: () => {
      invalidarMatriz();
      avisar('Perfil reativado.');
    },
    onError: (e) => setErro(mensagemDoErro(e)),
  });

  const excluir = useMutation({
    mutationFn: (id: string) => perfisApi.excluir(id),
    onSuccess: () => {
      invalidarMatriz();
      avisar('Perfil excluído.');
    },
    onError: (e) => setErro(mensagemDoErro(e)),
  });

  const trocarPerfil = useMutation({
    mutationFn: ({ membershipId, perfilId }: { membershipId: string; perfilId: string }) =>
      perfisApi.trocarPerfil(membershipId, perfilId),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['acessos', 'equipe'] });
      avisar('Perfil da pessoa atualizado.');
    },
    onError: (e) => setErro(mensagemDoErro(e)),
  });

  function tentarAlterarCelula(perfil: PerfilNaMatriz, recurso: Recurso, novoNivel: string) {
    setErro(null);
    // Cosmético: o MESMO cálculo que o servidor faz, só para não deixar
    // a pessoa nem tentar. Quem decide de verdade é o 403 do servidor.
    if (perfil.id === eu?.perfil.id) {
      const atual = nivelOuNegado(recurso, perfil.niveis[recurso]);
      const novo = NIVEIS[recurso][novoNivel];
      if (novo && ganho(atual, novo)) {
        avisar(
          'Isso aumentaria o seu próprio acesso. Peça a outra pessoa que edite permissões.',
          'aviso',
        );
        return;
      }
    }
    alterarCelula.mutate({ id: perfil.id, recurso, nivel: novoNivel });
  }

  async function exportarResumo() {
    setErro(null);
    try {
      const { nomeDoArquivo, conteudo } = await auditoriaApi.exportar({});
      baixarArquivo(nomeDoArquivo, conteudo);
      void cliente.invalidateQueries({ queryKey: ['auditoria'] });
    } catch (e) {
      setErro(mensagemDoErro(e));
    }
  }

  if (matrizQuery.isPending) {
    return (
      <>
        <Cabecalho podeCriar={false} onNovoPerfil={() => {}} />
        <EsqueletoDeCard linhas={6} />
      </>
    );
  }

  if (matrizQuery.isError) {
    return (
      <>
        <Cabecalho podeCriar={false} onNovoPerfil={() => {}} />
        <Erro tipo="permissao" onTentarDeNovo={() => void matrizQuery.refetch()} />
      </>
    );
  }

  const perfis = matrizQuery.data.perfis;

  return (
    <>
      <Cabecalho podeCriar={podeCriar} onNovoPerfil={() => setNovoPerfilAberto(true)} />

      <Nota>
        <b>Perfis e permissões.</b> Como o modelo é RBAC em tabela, criar um perfil novo (por
        exemplo &ldquo;Atendente do bar&rdquo;) é configuração, não desenvolvimento. Clique numa
        célula da matriz para trocar o nível — vale na requisição seguinte, sem reiniciar nada.
      </Nota>

      {erro && (
        <div className="empty" role="alert" style={{ borderColor: 'var(--red)' }}>
          {erro}
        </div>
      )}

      <Card
        titulo="Matriz de permissões"
        dica={podeEditar ? 'clique numa célula para alterar' : undefined}
      >
        <Tabela colunas={['Módulo / ação', ...perfis.map((p) => p.nome)]}>
          {RECURSOS.map((recurso) => (
            <tr key={recurso}>
              <td style={{ fontWeight: 600 }}>{ROTULO_RECURSO[recurso]}</td>
              {perfis.map((perfil) => (
                <td key={perfil.id}>
                  <select
                    className={`status ${classeDaCelula(perfil.niveis[recurso] ?? 'nao')}`}
                    value={perfil.niveis[recurso] ?? 'nao'}
                    disabled={!podeEditar || alterarCelula.isPending}
                    aria-label={`${ROTULO_RECURSO[recurso]} · ${perfil.nome}`}
                    onChange={(e) => tentarAlterarCelula(perfil, recurso, e.target.value)}
                  >
                    {Object.entries(NIVEIS[recurso]).map(([chave, nivel]) => (
                      <option key={chave} value={chave}>
                        {nivel.rotulo}
                      </option>
                    ))}
                  </select>
                </td>
              ))}
            </tr>
          ))}
        </Tabela>
      </Card>

      <Card titulo="Perfis desta arena" dica="criar, renomear, desativar ou excluir">
        <Tabela colunas={['Perfil', 'Membros', 'Status', 'Ações']}>
          {perfis.map((perfil) => (
            <tr key={perfil.id}>
              <td>
                {renomeando?.id === perfil.id ? (
                  <form
                    style={{ display: 'flex', gap: 6 }}
                    onSubmit={(e) => {
                      e.preventDefault();
                      if (renomeando.nome.trim().length >= 2) {
                        renomear.mutate({ id: perfil.id, nome: renomeando.nome.trim() });
                      }
                    }}
                  >
                    <input
                      autoFocus
                      value={renomeando.nome}
                      onChange={(e) => setRenomeando({ id: perfil.id, nome: e.target.value })}
                      aria-label={`Novo nome para ${perfil.nome}`}
                    />
                    <BotaoDeIcone icone="check" aria-label="Salvar nome" type="submit" />
                    <BotaoDeIcone
                      icone="x"
                      aria-label="Cancelar"
                      onClick={() => setRenomeando(null)}
                    />
                  </form>
                ) : (
                  <>
                    <b>{perfil.nome}</b>
                    {perfil.sistema && <small style={{ marginLeft: 6 }}>· de sistema</small>}
                  </>
                )}
              </td>
              <td className="num">{perfil.membros}</td>
              <td>
                <Status situacao={perfil.ativo ? 'ok' : 'neutra'}>
                  {perfil.ativo ? 'Ativo' : 'Desativado'}
                </Status>
              </td>
              <td>
                {podeEditar && renomeando?.id !== perfil.id && (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <Botao
                      variante="fantasma"
                      pequeno
                      onClick={() => setRenomeando({ id: perfil.id, nome: perfil.nome })}
                    >
                      Renomear
                    </Botao>
                    {perfil.ativo ? (
                      <Botao
                        variante="fantasma"
                        pequeno
                        onClick={() => desativar.mutate(perfil.id)}
                      >
                        Desativar
                      </Botao>
                    ) : (
                      <Botao variante="fantasma" pequeno onClick={() => reativar.mutate(perfil.id)}>
                        Reativar
                      </Botao>
                    )}
                    {podeExcluir && !perfil.sistema && (
                      <Botao
                        variante="fantasma"
                        pequeno
                        onClick={() => {
                          if (
                            window.confirm(
                              `Excluir "${perfil.nome}"? Só funciona se ninguém estiver vinculado a ele.`,
                            )
                          ) {
                            excluir.mutate(perfil.id);
                          }
                        }}
                      >
                        Excluir
                      </Botao>
                    )}
                  </div>
                )}
              </td>
            </tr>
          ))}
        </Tabela>
      </Card>

      <Card
        titulo="Equipe"
        dica={
          podeEditar
            ? 'quem está num perfil desativado aparece como suspenso — mova para outro perfil'
            : undefined
        }
      >
        {equipeQuery.isPending && <EsqueletoDeCard linhas={3} />}
        {equipeQuery.isError && <Erro detalhe="Não consegui ler a equipe." />}
        {equipeQuery.data && equipeQuery.data.length === 0 && (
          <Vazio>Ninguém vinculado ainda.</Vazio>
        )}
        {equipeQuery.data && equipeQuery.data.length > 0 && (
          <Tabela
            colunas={[
              'Pessoa',
              'Perfil',
              'Situação',
              'Segundo fator',
              'Último login',
              ...(podeEditar ? ['Senha'] : []),
            ]}
          >
            {equipeQuery.data.map((membro) => (
              <tr key={membro.membershipId}>
                <td>
                  <Quem nome={membro.nome} detalhe={membro.email} />
                </td>
                <td>
                  {podeEditar ? (
                    <select
                      value={membro.perfilId}
                      disabled={trocarPerfil.isPending}
                      aria-label={`Perfil de ${membro.nome}`}
                      onChange={(e) =>
                        trocarPerfil.mutate({
                          membershipId: membro.membershipId,
                          perfilId: e.target.value,
                        })
                      }
                    >
                      {perfis.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.nome}
                        </option>
                      ))}
                    </select>
                  ) : (
                    (perfis.find((p) => p.id === membro.perfilId)?.nome ?? '—')
                  )}
                </td>
                <td>
                  <Status situacao={membro.ativa ? 'ok' : 'atencao'}>
                    {membro.ativa ? 'Normal' : 'Perfil desativado'}
                  </Status>
                </td>
                <td>{membro.segundoFator ? 'Ativo' : '—'}</td>
                <td className="sub">
                  {membro.ultimoLogin
                    ? quandoLocal(
                        membro.ultimoLogin,
                        auditoriaQuery.data?.fuso ?? 'America/Sao_Paulo',
                      )
                    : 'nunca'}
                </td>
                {podeEditar && (
                  <td>
                    <Botao
                      variante="fantasma"
                      pequeno
                      onClick={() => setCodigoPara(membro)}
                      title="Gera um código de uso único para a pessoa trocar a senha"
                    >
                      Gerar código
                    </Botao>
                  </td>
                )}
              </tr>
            ))}
          </Tabela>
        )}
      </Card>

      <div className="grid g2">
        <Card titulo="Segurança">
          {segurancaQuery.isPending && <EsqueletoDeCard linhas={3} />}
          {segurancaQuery.isError && <Erro detalhe="Não consegui ler a situação de segurança." />}
          {segurancaQuery.data && (
            <Feed>
              <ItemDeFeed
                marca={
                  <span className="sq" style={{ background: 'var(--serie-5)' }}>
                    <Icone nome="lock" />
                  </span>
                }
                titulo={<b>Senha com hash {segurancaQuery.data.senha.algoritmo}</b>}
                subtitulo="nunca armazenada em texto plano"
                direita={<Status situacao="ok">Ativo</Status>}
              />
              <ItemDeFeed
                marca={
                  <span className="sq" style={{ background: 'var(--serie-5)' }}>
                    <Icone nome="lock" />
                  </span>
                }
                titulo={<b>Segundo fator</b>}
                subtitulo={`${segurancaQuery.data.segundoFator.ativados} de ${segurancaQuery.data.segundoFator.total} pessoas · opcional, por aplicativo autenticador`}
                direita={
                  <button
                    type="button"
                    className="link"
                    onClick={() => setSegundoFatorAberto(true)}
                  >
                    Gerenciar o meu
                  </button>
                }
              />
              <ItemDeFeed
                marca={
                  <span className="sq" style={{ background: 'var(--serie-5)' }}>
                    <Icone nome="lock" />
                  </span>
                }
                titulo={
                  <b>
                    Sessão do seu perfil expira em{' '}
                    {segurancaQuery.data.sessao.porPerfil.find((p) => p.chave === eu?.perfil.chave)
                      ?.efetivas ?? '—'}{' '}
                    horas
                  </b>
                }
                subtitulo="configurável por perfil, em Configurações da arena"
                direita={<Status situacao="ok">Ativo</Status>}
              />
            </Feed>
          )}
        </Card>

        <Card
          titulo="Registro de auditoria (LGPD)"
          acao={
            <button type="button" className="link" onClick={() => void exportarResumo()}>
              Exportar
            </button>
          }
        >
          {auditoriaQuery.isPending && <EsqueletoDeCard linhas={3} />}
          {auditoriaQuery.isError && <Erro detalhe="Não consegui ler a trilha." />}
          {auditoriaQuery.data && auditoriaQuery.data.itens.length === 0 && (
            <Vazio>Nenhum evento ainda.</Vazio>
          )}
          {auditoriaQuery.data && auditoriaQuery.data.itens.length > 0 && (
            <Feed>
              {auditoriaQuery.data.itens.map((evento) => (
                <ItemDeFeed
                  key={evento.id}
                  marca={<Avatar nome={evento.autor?.nome ?? '?'} />}
                  titulo={tituloDoEvento(evento)}
                  subtitulo={quandoLocal(evento.ocorridoEm, auditoriaQuery.data.fuso)}
                />
              ))}
            </Feed>
          )}
          <div style={{ marginTop: 12 }}>
            <button type="button" className="link" onClick={() => navegar('/auditoria')}>
              Ver console completo
            </button>
          </div>
        </Card>
      </div>

      {novoPerfilAberto && (
        <ModalDeNovoPerfil
          perfis={perfis}
          onFechar={() => setNovoPerfilAberto(false)}
          onCriado={() => {
            setNovoPerfilAberto(false);
            invalidarMatriz();
            avisar('Perfil criado. Já disponível na tela de login.');
          }}
        />
      )}

      {segundoFatorAberto && <ModalDeSegundoFator onFechar={() => setSegundoFatorAberto(false)} />}

      {codigoPara && (
        <ModalDeCodigoDeSenha membro={codigoPara} onFechar={() => setCodigoPara(null)} />
      )}
    </>
  );
}

function Cabecalho({ podeCriar, onNovoPerfil }: { podeCriar: boolean; onNovoPerfil: () => void }) {
  return (
    <div className="page-head">
      <div>
        <h1>Acessos e perfis</h1>
        <p>Permissões em tabela, sem precisar de nova publicação.</p>
      </div>
      {podeCriar && (
        <div className="head-actions">
          <Botao icone="plus" onClick={onNovoPerfil}>
            Novo perfil
          </Botao>
        </div>
      )}
    </div>
  );
}
